import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Avatar, AvatarImage, AvatarFallback } from "../../components/ui/avatar";
import { VOICE_STATUS } from "./useVoicePipeline";
import { Mic, MicOff, Volume2, Loader2, Signal } from "lucide-react";

function getMicButtonState(isMuted, voiceStatus) {
  if (isMuted || voiceStatus === VOICE_STATUS.MUTED)
    return { bg: 'bg-slate-500', ring: '', icon: MicOff, pulse: false, glow: false, spin: false, size: 'w-28 h-28', iconSize: 'w-12 h-12' };
  switch (voiceStatus) {
    case VOICE_STATUS.LISTENING:
      return { bg: 'bg-[#14B8A6]', ring: 'ring-4 ring-[#14B8A6]/30', icon: Mic, pulse: true, glow: true, spin: false, size: 'w-32 h-32', iconSize: 'w-14 h-14' };
    case VOICE_STATUS.THINKING:
      return { bg: 'bg-[#F59E0B]', ring: 'ring-4 ring-[#F59E0B]/30', icon: Loader2, pulse: false, glow: false, spin: true, size: 'w-28 h-28', iconSize: 'w-12 h-12' };
    case VOICE_STATUS.SPEAKING:
      return { bg: 'bg-[#2563EB]', ring: 'ring-4 ring-[#2563EB]/30', icon: Volume2, pulse: true, glow: true, spin: false, size: 'w-28 h-28', iconSize: 'w-12 h-12' };
    default:
      return { bg: 'bg-[#0B1F3B] hover:bg-[#0B1F3B]/90', ring: '', icon: Mic, pulse: false, glow: false, spin: false, size: 'w-28 h-28', iconSize: 'w-12 h-12' };
  }
}

function Waveform({ color, count = 7 }) {
  return (
    <div className="flex items-end justify-center gap-1 mt-4 h-8">
      {[...Array(count)].map((_, i) => (
        <div key={`w-${i}`} className={`w-1.5 ${color} rounded-full waveform-bar`} style={{ animationDelay: `${i * 0.1}s`, height: '8px' }} />
      ))}
    </div>
  );
}

function getStatusBadgeStyle(voiceStatus) {
  if (voiceStatus === VOICE_STATUS.LISTENING) return 'bg-[#14B8A6]/10 text-[#14B8A6]';
  if (voiceStatus === VOICE_STATUS.THINKING) return 'bg-[#F59E0B]/10 text-[#F59E0B]';
  if (voiceStatus === VOICE_STATUS.SPEAKING) return 'bg-[#2563EB]/10 text-[#2563EB]';
  return 'bg-slate-100 text-slate-500';
}

function getStatusBadgeLabel(voiceStatus, isMuted) {
  if (voiceStatus === VOICE_STATUS.LISTENING) return 'Listening';
  if (voiceStatus === VOICE_STATUS.THINKING) return 'Processing';
  if (voiceStatus === VOICE_STATUS.SPEAKING) return 'AI Speaking';
  if (isMuted) return 'Muted';
  return 'Ready';
}

function getMicHint(voiceStatus, isMuted) {
  if (voiceStatus === VOICE_STATUS.LISTENING) return 'Tap to mute';
  if (voiceStatus === VOICE_STATUS.SPEAKING) return 'Tap to interrupt';
  if (isMuted) return 'Tap to unmute';
  return 'Tap to start speaking';
}

export function VoiceSession({
  session, messages, voiceStatus, resistanceLevel, turnCount,
  isMuted, currentTranscript, messagesEndRef, selectedLanguage,
  onMicClick, onToggleMute
}) {
  const micState = getMicButtonState(isMuted, voiceStatus);
  const MicIcon = micState.icon;

  const getLanguageLabel = () => {
    switch (selectedLanguage) { case 'hinglish': return 'Hinglish'; case 'hi': return 'Hindi'; default: return 'English'; }
  };

  const isHindiLike = selectedLanguage === 'hi' || selectedLanguage === 'hinglish';
  const getStatusText = () => {
    if (isMuted || voiceStatus === VOICE_STATUS.MUTED) return isHindiLike ? 'Mic muted hai' : 'Microphone Muted';
    switch (voiceStatus) {
      case VOICE_STATUS.LISTENING: return isHindiLike ? 'Sun raha hai... bolo' : 'Listening... speak now';
      case VOICE_STATUS.THINKING: return isHindiLike ? 'Soch raha hai...' : 'Processing...';
      case VOICE_STATUS.SPEAKING: return isHindiLike ? 'Customer bol raha hai...' : 'Customer is speaking...';
      default: return isHindiLike ? 'Mic dabao aur bolo' : 'Tap mic to speak';
    }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-6 h-[calc(100vh-180px)]">
      {/* Transcript Panel */}
      <div className="lg:w-80 flex-shrink-0 order-2 lg:order-1">
        <Card className="h-full flex flex-col premium-card">
          <CardHeader className="pb-2 flex-shrink-0 border-b border-slate-100">
            <CardTitle className="text-sm font-semibold text-[#0B1F3B] flex items-center gap-2">
              <div className="w-2 h-2 bg-[#14B8A6] rounded-full recording-indicator" />
              Live Transcript
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full px-4 pb-4 pt-3">
              <div className="space-y-3">
                {messages.map((msg, idx) => (
                  <div
                    key={`msg-${idx}-${msg.role}`}
                    className={`p-3 rounded-xl ${msg.role === 'user' ? 'bg-[#0B1F3B] text-white ml-4' : 'bg-[#2563EB]/5 text-[#0B1F3B] mr-4 border border-[#2563EB]/10'}`}
                    data-testid={`transcript-msg-${idx}`}
                  >
                    <p className="text-xs font-medium mb-1 opacity-70">{msg.role === 'user' ? 'You' : session.persona?.name}</p>
                    <p className="text-sm leading-relaxed">{msg.content}</p>
                  </div>
                ))}
                {currentTranscript && (
                  <div className="p-3 rounded-xl bg-[#14B8A6]/10 text-[#0B1F3B] ml-4 border-2 border-[#14B8A6]/30 border-dashed">
                    <p className="text-xs font-medium mb-1 text-[#14B8A6]">Speaking...</p>
                    <p className="text-sm">{currentTranscript}</p>
                  </div>
                )}
                {voiceStatus === VOICE_STATUS.THINKING && (
                  <div className="p-3 rounded-xl bg-[#F59E0B]/10 text-[#0B1F3B] mr-4 border border-[#F59E0B]/20">
                    <p className="text-xs font-medium mb-1 text-[#F59E0B]">{session.persona?.name}</p>
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-[#F59E0B] rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-[#F59E0B] rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                      <div className="w-2 h-2 bg-[#F59E0B] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Voice Interface */}
      <div className="flex-1 flex flex-col order-1 lg:order-2">
        <Card className="flex-1 flex flex-col premium-card overflow-hidden">
          {/* Persona Info */}
          <div className="p-6 border-b border-slate-200/60 flex-shrink-0 bg-[#0B1F3B]/[0.02]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Avatar className="w-14 h-14 border-2 border-white shadow-lg">
                  <AvatarImage src={session.persona?.avatar} />
                  <AvatarFallback className="bg-[#2563EB] text-white font-bold">{session.persona?.name?.[0]}</AvatarFallback>
                </Avatar>
                <div>
                  <h2 className="text-lg font-bold text-[#0B1F3B]">{session.persona?.name}</h2>
                  <p className="text-sm text-slate-500">{session.persona?.occupation}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs border-[#2563EB]/20 text-[#2563EB]">{session.scenario?.name}</Badge>
                    {session.persona?.voice_config && (
                      <Badge variant="outline" className="text-xs border-[#14B8A6]/20 text-[#14B8A6]">
                        <Volume2 className="w-3 h-3 mr-1" />{session.persona.voice_config.voice_label?.split(' ')[0]}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-1"><Signal className="w-3 h-3" /> Resistance</div>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${resistanceLevel <= 3 ? 'bg-[#14B8A6]' : resistanceLevel <= 6 ? 'bg-[#F59E0B]' : 'bg-red-500'}`}
                      style={{ width: `${resistanceLevel * 10}%` }}
                    />
                  </div>
                  <span className="text-sm font-bold text-[#0B1F3B]">{resistanceLevel}/10</span>
                </div>
              </div>
            </div>
          </div>

          {/* Mic Button */}
          <div className="flex-1 flex flex-col items-center justify-center p-8" data-testid="mic-area">
            <div className={`mb-6 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase ${getStatusBadgeStyle(voiceStatus)}`}>
              {getStatusBadgeLabel(voiceStatus, isMuted)}
            </div>

            <div className="relative">
              {micState.pulse && (
                <>
                  <div className={`absolute inset-0 ${micState.bg} rounded-full opacity-20 animate-ping`} />
                  <div className={`absolute -inset-4 ${micState.bg} rounded-full opacity-10 animate-pulse`} />
                  <div className={`absolute -inset-8 ${micState.bg} rounded-full opacity-5 animate-pulse`} style={{ animationDelay: '0.3s' }} />
                </>
              )}
              <button
                onClick={onMicClick}
                disabled={voiceStatus === VOICE_STATUS.THINKING}
                className={`${micState.size} ${micState.bg} ${micState.ring} rounded-full flex items-center justify-center text-white shadow-2xl transition-all duration-300 ease-out hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed relative z-10 ${micState.glow ? 'mic-glow' : ''}`}
                data-testid="main-mic-btn"
              >
                <MicIcon className={`${micState.iconSize} ${micState.spin ? 'animate-spin' : ''}`} />
              </button>
            </div>

            <p className="mt-6 text-base font-medium text-[#0B1F3B]" data-testid="voice-status-text">{getStatusText()}</p>
            {voiceStatus === VOICE_STATUS.LISTENING && <Waveform color="bg-[#14B8A6]" />}
            {voiceStatus === VOICE_STATUS.SPEAKING && <Waveform color="bg-[#2563EB]" />}
            <p className="mt-4 text-xs text-slate-400">
              {getMicHint(voiceStatus, isMuted)}
            </p>
          </div>

          {/* Bottom Bar */}
          <div className="p-4 border-t border-slate-200/60 bg-slate-50/50 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Badge variant="outline" className="text-xs border-[#2563EB]/20 text-[#2563EB] bg-[#2563EB]/5">{getLanguageLabel()}</Badge>
                <div className="text-sm text-slate-500">Turn: <span className="font-bold text-[#0B1F3B]">{turnCount}</span></div>
              </div>
              <Button
                variant={isMuted ? "destructive" : "outline"} size="sm" onClick={onToggleMute}
                className={isMuted ? '' : 'border-slate-200 text-slate-600 hover:text-[#0B1F3B]'}
                data-testid="mute-toggle-btn"
              >
                {isMuted ? <><MicOff className="w-4 h-4 mr-1" /> Unmute</> : <><Mic className="w-4 h-4 mr-1" /> Mute</>}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
